import { ApiResponse } from 'apisauce';

import { network } from './network';
import { LoginRequest, LoginResponse, TokenResponse } from '../types/authTypes';
import {
  AdminAnalyticsResponse,
  AdminAnalyticsTimePeriod,
  AutoPlanFeedbackPageable,
  CancelSubscriptionResponse,
  CoachMembershipDto,
  CreateOrUpdateOrganizationRequest,
  ImportUsersInOrganizationResponse,
  InviteUserRequest,
  MembershipPageable,
  OrganizationsPageable,
  OrganizationT,
  ResetTrialAnalyticsResponse,
  ScheduledEmailsPageable,
  UpdateUserRequest,
  User,
  UsersPageable
} from '../types/userTypes';

export interface Api {
  login: (request: LoginRequest) => Promise<ApiResponse<LoginResponse>>;
  getAllSubscribedUsers: (queryParams: any) => Promise<ApiResponse<UsersPageable>>;
  getAllInvitedUsers: (queryParams: any) => Promise<ApiResponse<UsersPageable>>;
  getAllCoaches: (queryParams: any) => Promise<ApiResponse<UsersPageable>>;
  getCoachMemberships: (id: number) => Promise<ApiResponse<CoachMembershipDto[]>>;
  assignCoach: (id: number, accessCode: string) => Promise<ApiResponse<void>>;
  updateUser: (request: UpdateUserRequest) => Promise<ApiResponse<UpdateUserRequest>>;
  inviteUser: (request: InviteUserRequest) => Promise<ApiResponse<void>>;
  getAllOrganizations: (queryParams: any) => Promise<ApiResponse<OrganizationsPageable>>;
  createOrganization: (request: CreateOrUpdateOrganizationRequest) => Promise<ApiResponse<OrganizationT>>;
  updateOrganization: (id: number, request: CreateOrUpdateOrganizationRequest) => Promise<ApiResponse<OrganizationT>>;
  getUsersWithinOrganization: (id: number, queryParams: any) => Promise<ApiResponse<User[]>>;
  importUsersInOrganization: (id: number, file: any) => Promise<ApiResponse<ImportUsersInOrganizationResponse>>;
  resendUserInvite: (id: number) => Promise<ApiResponse<void>>;
  resendUserActivation: (id: number) => Promise<ApiResponse<void>>;
  resendUserEmailVerification: (id: number) => Promise<ApiResponse<void>>;
  getCancellations: (queryParams: any) => Promise<ApiResponse<UsersPageable>>;
  getAutoPlanFeedback: (queryParams: any) => Promise<ApiResponse<AutoPlanFeedbackPageable>>;
  getPendingMemberships: (queryParams: any) => Promise<ApiResponse<MembershipPageable>>;
  cancelSubscription: (id: number) => Promise<ApiResponse<CancelSubscriptionResponse>>;
  grantLifetimeAccess: (id: number) => Promise<ApiResponse<void>>;
  grantDemoAccess: (id: number, accessUntil: string) => Promise<ApiResponse<string>>;
  analytics: (period: AdminAnalyticsTimePeriod) => Promise<ApiResponse<AdminAnalyticsResponse>>;
  getResetTrials: () => Promise<ApiResponse<ResetTrialAnalyticsResponse>>;
  getAllUnredeemedUsers: (queryParams: any) => Promise<ApiResponse<UsersPageable>>;
  getScheduledEmails: (queryParams: any) => Promise<ApiResponse<ScheduledEmailsPageable>>;
  changeUserEmail: (id: number, email: string) => Promise<ApiResponse<void>>;
  generateUserResetPasswordCode: (id: number) => Promise<ApiResponse<string>>;
  deleteAccount: (id: number) => Promise<ApiResponse<void>>;
  initCoachStudentSession: (studentId: number) => Promise<ApiResponse<TokenResponse>>;
}

const login = (request: LoginRequest) => network.post<LoginResponse>('/sign-in/admin', request);
const getAllSubscribedUsers = (queryParams: any) => network.get<UsersPageable>(`/users/subscribed?${queryParams}`);
const getAllInvitedUsers = (queryParams: any) => network.get<UsersPageable>(`/users/invited?${queryParams}`);
const getAllCoaches = (queryParams: any) => network.get<UsersPageable>(`/users/coaches?${queryParams}`);
const getCoachMemberships = (id: number) => network.get<CoachMembershipDto[]>(`/users/coaches/${id}/memberships`);
const assignCoach = (id: number, accessCode: string) => network.post<void>(`/users/${id}/assign-coach`, { accessCode });
const updateUser = (request: UpdateUserRequest) => network.patch<UpdateUserRequest>(`/users`, request);
const inviteUser = (request: InviteUserRequest) => network.post<void>(`/users/invite`, request);
const getAllOrganizations = (queryParams: any) => network.get<OrganizationsPageable>(`/organizations?${queryParams}`);
const createOrganization = (request: CreateOrUpdateOrganizationRequest) =>
  network.post<OrganizationT>('/organizations', request);
const updateOrganization = (id: number, request: CreateOrUpdateOrganizationRequest) =>
  network.post<OrganizationT>(`/organizations/${id}`, request);
const getUsersWithinOrganization = (id: number, queryParams: any) =>
  network.get<User[]>(`/organizations/${id}/users?${queryParams}`);
const importUsersInOrganization = (id: number, file: any) => {
  const formData = new FormData();
  formData.append('usersFile', file);
  return network.post<ImportUsersInOrganizationResponse>(`/organizations/${id}/users`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data'
    }
  });
};
const resendUserInvite = (id: number) => network.post<void>(`/users/${id}/resend`);
const resendUserActivation = (id: number) => network.post<void>(`/users/${id}/resend-activation`);
const resendUserEmailVerification = (id: number) => network.post<void>(`/users/${id}/resend-verification`);
const getCancellations = (queryParams: any) => network.get<UsersPageable>(`/admin/cancellations?${queryParams}`);

const getAutoPlanFeedback = (queryParams: any) =>
  network.get<AutoPlanFeedbackPageable>(`/admin/auto-plan-feedback?${queryParams}`);
const getPendingMemberships = (queryParams: any) =>
  network.get<MembershipPageable>(`/admin/pending-memberships?${queryParams}`);
const cancelSubscription = (id: number) => network.post<CancelSubscriptionResponse>(`/membership/stripe/${id}/cancel`);
const grantLifetimeAccess = (id: number) => network.post<void>(`/users/${id}/grant-lifetime`);
const grantDemoAccess = (id: number, accessUntil: string) => network.post<string>(`/users/${id}/grant-demo`, accessUntil);
const analytics = (period: AdminAnalyticsTimePeriod) => network.get<AdminAnalyticsResponse>('/admin/analytics', { period });
const getResetTrials = () => network.get<ResetTrialAnalyticsResponse>('/admin/reset-trials');
const getAllUnredeemedUsers = (queryParams: any) => network.get<UsersPageable>(`/users/unredeemed?${queryParams}`);
const getScheduledEmails = (queryParams: any) =>
  network.get<ScheduledEmailsPageable>(`/admin/scheduled-emails?${queryParams}`);
const changeUserEmail = (id: number, email: string) => network.post<void>(`/users/${id}/change-email`, { email });
const generateUserResetPasswordCode = (id: number) => network.post<string>(`/users/${id}/generate-reset-password-code`);
const deleteAccount = (id: number) => network.delete<void>(`/users/${id}/account`);
const initCoachStudentSession = (studentId: number) =>
  network.post<TokenResponse>(`/admin/students/${studentId}/init-session`);

export const api: Api = {
  login,
  getAllSubscribedUsers,
  getAllInvitedUsers,
  getAllCoaches,
  getCoachMemberships,
  assignCoach,
  updateUser,
  inviteUser,
  getAllOrganizations,
  createOrganization,
  updateOrganization,
  getUsersWithinOrganization,
  importUsersInOrganization,
  resendUserInvite,
  resendUserActivation,
  resendUserEmailVerification,
  getCancellations,
  getAutoPlanFeedback,
  getPendingMemberships,
  cancelSubscription,
  grantLifetimeAccess,
  grantDemoAccess,
  analytics,
  getResetTrials,
  getAllUnredeemedUsers,
  getScheduledEmails,
  changeUserEmail,
  generateUserResetPasswordCode,
  deleteAccount,
  initCoachStudentSession
};
